import * as ES from './ecmascript';
import { MakeIntrinsicClass } from './intrinsicclass';
import { ISO_MONTH, ISO_DAY, ISO_YEAR, CALENDAR, GetSlot } from './slots';
import { DateTimeFormat } from './intl';
const ObjectCreate = Object.create;
export class PlainMonthDay {
    constructor(isoMonthParam, isoDayParam, calendarParam = ES.GetISO8601Calendar(), referenceISOYearParam = 1972) {
        const isoMonth = ES.ToIntegerThrowOnInfinity(isoMonthParam);
        const isoDay = ES.ToIntegerThrowOnInfinity(isoDayParam);
        const calendar = ES.ToTemporalCalendar(calendarParam);
        const referenceISOYear = ES.ToIntegerThrowOnInfinity(referenceISOYearParam);
        // Note: if the arguments are not passed,
        //       ToIntegerThrowOnInfinity(undefined) will have returned 0, which will
        //       be rejected by RejectISODate in CreateTemporalMonthDaySlots. This
        //       check exists only to improve the error message.
        if (arguments.length < 2) {
            throw new RangeError('missing argument: isoMonth and isoDay are required');
        }
        ES.CreateTemporalMonthDaySlots(this, isoMonth, isoDay, calendar, referenceISOYear);
    }
    get monthCode() {
        if (!ES.IsTemporalMonthDay(this))
            throw new TypeError('invalid receiver');
        return ES.CalendarMonthCode(GetSlot(this, CALENDAR), this);
    }
    get day() {
        if (!ES.IsTemporalMonthDay(this))
            throw new TypeError('invalid receiver');
        return ES.CalendarDay(GetSlot(this, CALENDAR), this);
    }
    get calendar() {
        if (!ES.IsTemporalMonthDay(this))
            throw new TypeError('invalid receiver');
        return GetSlot(this, CALENDAR);
    }
    with(temporalMonthDayLike, optionsParam = undefined) {
        if (!ES.IsTemporalMonthDay(this))
            throw new TypeError('invalid receiver');
        if (!ES.IsObject(temporalMonthDayLike)) {
            throw new TypeError('invalid argument');
        }
        ES.RejectObjectWithCalendarOrTimeZone(temporalMonthDayLike);
        const calendar = GetSlot(this, CALENDAR);
        const fieldNames = ES.CalendarFields(calendar, ['day', 'month', 'monthCode', 'year']);
        const props = ES.PrepareTemporalFields(temporalMonthDayLike, fieldNames, 'partial');
        if (!props) {
            throw new TypeError('invalid month-day-like');
        }
        let fields = ES.PrepareTemporalFields(this, fieldNames, []);
        fields = ES.CalendarMergeFields(calendar, fields, props);
        fields = ES.PrepareTemporalFields(fields, fieldNames, []);
        const options = ES.GetOptionsObject(optionsParam);
        return ES.CalendarMonthDayFromFields(calendar, fields, options);
    }
    equals(otherParam) {
        if (!ES.IsTemporalMonthDay(this))
            throw new TypeError('invalid receiver');
        const other = ES.ToTemporalMonthDay(otherParam);
        for (const slot of [ISO_MONTH, ISO_DAY, ISO_YEAR]) {
            const val1 = GetSlot(this, slot);
            const val2 = GetSlot(other, slot);
            if (val1 !== val2)
                return false;
        }
        return ES.CalendarEquals(GetSlot(this, CALENDAR), GetSlot(other, CALENDAR));
    }
    toString(optionsParam = undefined) {
        if (!ES.IsTemporalMonthDay(this))
            throw new TypeError('invalid receiver');
        const options = ES.GetOptionsObject(optionsParam);
        const showCalendar = ES.ToShowCalendarOption(options);
        return ES.TemporalMonthDayToString(this, showCalendar);
    }
    toJSON() {
        if (!ES.IsTemporalMonthDay(this))
            throw new TypeError('invalid receiver');
        return ES.TemporalMonthDayToString(this);
    }
    toLocaleString(locales = undefined, options = undefined) {
        if (!ES.IsTemporalMonthDay(this))
            throw new TypeError('invalid receiver');
        return new DateTimeFormat(locales, options).format(this);
    }
    valueOf() {
        throw new TypeError('use equals() to compare Temporal.PlainMonthDay');
    }
    toPlainDate(item) {
        if (!ES.IsTemporalMonthDay(this))
            throw new TypeError('invalid receiver');
        if (!ES.IsObject(item))
            throw new TypeError('argument should be an object');
        const calendar = GetSlot(this, CALENDAR);
        const receiverFieldNames = ES.CalendarFields(calendar, ['day', 'monthCode']);
        const fields = ES.PrepareTemporalFields(this, receiverFieldNames, []);
        const inputFieldNames = ES.CalendarFields(calendar, ['year']);
        const inputFields = ES.PrepareTemporalFields(item, inputFieldNames, []);
        let mergedFields = ES.CalendarMergeFields(calendar, fields, inputFields);
        // TODO: Use MergeLists abstract operation.
        const mergedFieldNames = [...new Set([...receiverFieldNames, ...inputFieldNames])];
        mergedFields = ES.PrepareTemporalFields(mergedFields, mergedFieldNames, []);
        const options = ObjectCreate(null);
        options.overflow = 'reject';
        return ES.CalendarDateFromFields(calendar, mergedFields, options);
    }
    getISOFields() {
        if (!ES.IsTemporalMonthDay(this))
            throw new TypeError('invalid receiver');
        return {
            calendar: GetSlot(this, CALENDAR),
            isoDay: GetSlot(this, ISO_DAY),
            isoMonth: GetSlot(this, ISO_MONTH),
            isoYear: GetSlot(this, ISO_YEAR)
        };
    }
    static from(item, optionsParam = undefined) {
        const options = ES.GetOptionsObject(optionsParam);
        if (ES.IsTemporalMonthDay(item)) {
            ES.ToTemporalOverflow(options); // validate and ignore
            return ES.CreateTemporalMonthDay(GetSlot(item, ISO_MONTH), GetSlot(item, ISO_DAY), GetSlot(item, CALENDAR), GetSlot(item, ISO_YEAR));
        }
        return ES.ToTemporalMonthDay(item, options);
    }
}
Symbol.toStringTag;
MakeIntrinsicClass(PlainMonthDay, 'Temporal.PlainMonthDay');
//# sourceMappingURL=plainmonthday.js.map